import { Button } from '@/components/ui/button';
import { useOptionalWorkspace } from '@/workspace/context';
import { isSettingsView, type WorkspaceView } from '@/workspace/view-definitions';
import { SETTINGS_ACTION_TYPES } from './settings-actions-copy';
import type { ActionView } from './action-confirmation';
import { useNamespaceCopy } from '@/i18n/ns';
import { entrySurfaceCopy } from '@/i18n/ns/entry-surfaces';

/** Only code-owned Settings operations may link a receipt to a secure form. */
export function SettingsActionOutcome({
  action,
  openLabel,
}: {
  action: ActionView;
  openLabel: string;
}) {
  const workspace = useOptionalWorkspace();
  const copy = useNamespaceCopy(entrySurfaceCopy);
  if (!Object.hasOwn(SETTINGS_ACTION_TYPES, action.tool)) return null;
  if (action.tool === 'open_protected_canvas') {
    const value = (
      action.result as {
        canvas?: { kind?: string; id?: string; title?: string; tab?: string };
      } | null
    )?.canvas;
    if (
      typeof value?.id !== 'string' ||
      !value.id ||
      (value.kind !== 'agent' && value.kind !== 'meeting')
    )
      return null;
    const canvas = {
      kind: value.kind,
      id: value.id,
      ...(value.kind === 'agent' ? { tab: 'access' } : {}),
    } as const;
    return (
      <Button type="button" variant="outline" onClick={() => workspace?.openCanvas(canvas)}>
        {openLabel}
        {value.title ? ` · ${value.title}` : ''}
      </Button>
    );
  }
  const result = action.result as {
    settingsView?: WorkspaceView;
    invitation?: { email?: string; url?: string };
  } | null;
  const view = result?.settingsView;
  if (
    !view ||
    !['account', 'preferences', 'voice', 'profile', 'team', 'billing'].includes(view) ||
    !isSettingsView(view)
  )
    return null;
  return (
    <>
      {action.tool === 'invite_company_member' && typeof result?.invitation?.url === 'string' && (
        <label>
          {copy.invitationLink}
          <input
            aria-label={copy.invitationLink}
            readOnly
            value={result.invitation.url}
            onFocus={(event) => event.target.select()}
          />
        </label>
      )}
      <Button type="button" variant="outline" onClick={() => workspace?.navigate(view)}>
        {openLabel}
      </Button>
    </>
  );
}
